// ─── Auth ────────────────────────────────────────────────────────────────────
export const AUTH = {
  APP_NAME: 'Awgment',
  // Brand panel (wide screens only)
  EYEBROW: 'AI-assisted modernization',
  HEADLINE: 'Modernize legacy systems with AI',
  TAGLINE:
    'Go from an existing codebase to tested, ready-to-ship code in one guided workspace.',
  // Keyed by WORKSPACE_STEPS so the panel always mirrors the real workflow
  STEP_HINTS: {
    Discovery: 'Explore the source files you upload',
    Blueprint: 'Design the target architecture with Awgment',
    Build: 'Generate code for your target stack',
    Validate: 'Generate test cases for the new code',
    Export: 'Download it.',
  } as Record<string, string>,
  // Sign-in form
  WELCOME_TITLE: 'Welcome back',
  SIGN_IN_SUBTITLE: 'Sign in to your Awgment workspace',
  EMAIL_LABEL: 'Email address',
  EMAIL_PLACEHOLDER: 'name@company.com',
  PASSWORD_LABEL: 'Password',
  PASSWORD_PLACEHOLDER: 'Enter your password',
  SHOW_PASSWORD: 'Show password',
  HIDE_PASSWORD: 'Hide password',
  CAPS_LOCK_ON: 'Caps Lock is on',
  SIGN_IN_BTN: 'Sign in',
  SIGNING_IN_BTN: 'Signing in…',
  HELP_TEXT: 'Trouble signing in? Contact your workspace administrator.',
  // Login failures, mapped from the HTTP response in lib/auth
  ERR_INVALID_CREDENTIALS: 'Incorrect email or password.',
  ERR_SERVICE_DOWN:
    'The sign-in service isn’t available right now. Try again in a moment.',
  ERR_NETWORK: 'Can’t reach the server. Check your connection and try again.',
  ERR_GENERIC: 'Sign-in failed. Please try again.',
} as const

// ─── Navigation / Sidebar ────────────────────────────────────────────────────
export const SIDEBAR = {
  APP_NAME: 'Awgment',
  EXPAND: 'Expand sidebar',
  COLLAPSE: 'Collapse sidebar',
  BACK_TO_DASHBOARD: 'Back to Dashboard',
  SEARCH_PLACEHOLDER: 'Search files...',
  SELECT_ALL: 'All',
  SELECT_NONE: 'None',
  NO_FILES: 'No files uploaded yet',
  DEFAULT_PROJECT: 'Project',
} as const

export const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Awgment Health', path: '/awgment-health' },
  { label: 'Semantic Layer', path: '/semantic-layer' },
] as const

// ─── Navbar ───────────────────────────────────────────────────────────────────
export const NAVBAR = {
  AI_PANEL_TOOLTIP: 'Ask Awgment',
  NOTIFICATIONS_TOOLTIP: 'Notifications',
} as const

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const DASHBOARD = {
  SEARCH_PLACEHOLDER: 'Search projects…',
  SEARCH_CLEAR: 'Clear search',
  STATUS_FILTER_LABEL: 'Filter projects by status',
  ALL_STATUSES: 'All',
  SORT_TOOLTIP: 'Sort projects',
  SORT_OPTIONS: [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'name', label: 'Name A–Z' },
    { value: 'priority', label: 'Priority' },
  ],
  LOADING: 'Loading projects…',
  GREETING: (name: string) => {
    const h = new Date().getHours()
    const part =
      h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
    return name ? `${part}, ${name}` : part
  },
  VIEW_LABEL: 'Project layout',
  VIEW_GRID: 'Grid view',
  VIEW_LIST: 'List view',
  GENERATE_PROJECT_BTN: 'New project',
  GENERATE_PROJECT_SHORT: 'New',
  RETRY_BTN: 'Try again',
  LOAD_FAILED_TITLE: 'Couldn’t load your projects',
  LOAD_FAILED_BODY: 'Check your connection, then try again.',
  NO_MATCH_TITLE: (q: string) => `No projects match “${q}”`,
  NO_MATCH_BODY: 'Try a different name or keyword.',
  CLEAR_SEARCH_BTN: 'Clear search',
  NO_STATUS_MATCH_TITLE: (status: string) =>
    `No ${status.toLowerCase()} projects`,
  NO_STATUS_MATCH_BODY: 'Pick another status to see more projects.',
  SHOW_ALL_BTN: 'Show all projects',
  EMPTY_TITLE: 'No projects yet',
  EMPTY_CTA: 'Create your first project',
  EMPTY_BODY:
    'Create your first project to start discovering and transforming code.',
  PROJECT_CREATED: (name: string) => `Project "${name}" created successfully`,
  PROJECT_CREATE_FAILED: (err: unknown) =>
    `Failed to create project: ${err ?? 'Unknown error'}`,
} as const

// ─── Dashboard — Stats cards ──────────────────────────────────────────────────
export const STAT_LABELS = {
  TOTAL_PROJECTS: 'Total Projects',
  ACTIVE_TRANSFORMATIONS: 'Active Transformations',
  COMPLETED_TRANSFORMATIONS: 'Completed Transformations',
  FILES_PROCESSED: 'Files Processed',
  LINES_TRANSFORMED: 'Lines Transformed',
  TOTAL_PROJECTS_SUB: 'Across all workspaces',
  ACTIVE_TRANSFORMATIONS_SUB: 'Currently running',
  COMPLETED_TRANSFORMATIONS_SUB: 'Finished runs',
  FILES_PROCESSED_SUB: 'Total files tracked',
  LINES_TRANSFORMED_SUB: 'Total lines processed',
  UNAVAILABLE: 'Couldn’t load this number. Refresh the page to try again.',
} as const

// ─── Project Card ─────────────────────────────────────────────────────────────
export const PROJECT_CARD = {
  LOADING_MSG: 'Project data is still loading. Please try again in a moment.',
  NO_ID_MSG: 'Cannot delete: project has no server ID',
  DELETED: (name: string) => `Project "${name}" deleted`,
  DELETE_FAILED: (err: unknown) => `Delete failed: ${err ?? 'Unknown error'}`,
  UPDATED: (name: string) => `Project "${name}" updated`,
  UPDATE_FAILED: (err: unknown) => `Update failed: ${err ?? 'Unknown error'}`,
  DELETING: 'Deleting...',
  DELETE_TITLE: 'Delete Project',
  DELETE_CONFIRM_PREFIX: 'Are you sure you want to delete',
  DELETE_CONFIRM_SUFFIX: 'This cannot be undone.',
  DELETE_BTN: 'Delete',
  CANCEL_BTN: 'Cancel',
  DATE_FALLBACK: 'N/A',
  CREATED_PREFIX: 'Created',
  NO_DESCRIPTION: 'No description',
  ACTIONS: (name: string) => `Actions for ${name}`,
  EDIT: 'Edit',
  COL_PROJECT: 'Project',
  COL_STATUS: 'Status',
  COL_PRIORITY: 'Priority',
  COL_MEMBERS: 'Members',
  COL_CREATED: 'Created',
} as const

// ─── Create Project Modal ─────────────────────────────────────────────────────
export const CREATE_PROJECT = {
  TITLE: 'Create New Project',
  NAME_LABEL: 'Project Name',
  NAME_PLACEHOLDER: 'e.g. Legacy Modernization',
  DESC_LABEL: 'Description',
  DESC_PLACEHOLDER: 'Enter a brief description of the project',
  PRIORITY_LABEL: 'Priority',
  COLLABORATORS_LABEL: 'Add Collaborators',
  USERS_PLACEHOLDER: 'Search users...',
  CANCEL_BTN: 'Cancel',
  CREATE_BTN: 'Create Project',
} as const

// ─── AI Panel ────────────────────────────────────────────────────────────────
export const AI_PANEL = {
  TITLE: 'Awgment',
  SUBTITLE: 'AI assistant',
  INPUT_PLACEHOLDER: 'Ask Awgment…',
  NEW_CHAT: 'New chat',
  CLOSE: 'Close assistant',
  SEND: 'Send message',
  EMPTY_TITLE: 'How can I help?',
  EMPTY_BODY: 'Ask about your projects, migrations or code.',
  THINKING: 'Thinking…',
  INPUT_HINT: 'Enter to send · Shift + Enter for a new line',
  SUGGESTIONS: [
    'Summarize my projects',
    'Which projects need attention?',
    'Suggest a refactoring strategy',
    'Explain the migration steps',
  ],
} as const

// ─── Profile Menu ─────────────────────────────────────────────────────────────
export const PROFILE_MENU = {
  MY_PROFILE: 'My Profile',
  SETTINGS: 'Settings',
  HELP_DOCS: 'Help & Docs',
  DARK_MODE: 'Dark Mode',
  SIGN_OUT: 'Sign out',
  DEFAULT_USER: 'User',
  DEFAULT_ROLE: 'viewer',
  DEFAULT_AVATAR: 'U',
} as const

// ─── Awgment Health route ─────────────────────────────────────────────────────
export const HEALTH_ROUTE = {
  CONNECTING: 'Connecting to Health Monitor…',
  FAILED: 'Couldn’t connect to the health monitor',
  RETRY: 'Try again',
} as const

// ─── Semantic Layer route ─────────────────────────────────────────────────────
export const SEMANTIC_LAYER_ROUTE = {
  LABEL: 'Semantic Layer',
  PATH: '/semantic-layer',
} as const

// ─── Projects route ───────────────────────────────────────────────────────────
export const PROJECTS_ROUTE = {
  TITLE: 'Projects',
  BODY: 'Full project management view coming soon. For now, browse projects from the Dashboard.',
} as const

// ─── Settings route ───────────────────────────────────────────────────────────
export const SETTINGS_ROUTE = {
  TITLE: 'Settings',
  SUBTITLE: 'Backend-backed workspace preferences and UI defaults.',
  SAVE_BTN: 'Save',
  SAVING_BTN: 'Saving...',
  INTERFACE_TITLE: 'Interface',
  INTERFACE_SUBTITLE: 'Control your preferred UI defaults.',
  TOGGLES_TITLE: 'Workspace Toggles',
  THEME_LABEL: 'Theme',
  THEME_DARK: 'Dark',
  THEME_LIGHT: 'Light',
  TARGET_LANG_LABEL: 'Default Target Language',
  SIDEBAR_LABEL: 'Collapse sidebar by default',
  AI_PANEL_LABEL: 'Open AI panel by default',
  NOTIF_LABEL: 'Enable notifications',
  LOADING: 'Loading settings...',
  SAVED_TOAST: 'Settings saved',
  LOAD_FAIL: 'Unable to load settings',
  SAVE_FAIL: 'Unable to save settings',
} as const

// ─── History route ────────────────────────────────────────────────────────────
export const HISTORY_ROUTE = {
  TITLE: 'Transformation History',
  SUBTITLE:
    'Live audit trail from the backend transformation history collection.',
  SECTION_TITLE: 'Recent Runs',
  REFRESH_BTN: 'Refresh',
  LOADING: 'Loading transformation history...',
  EMPTY: 'No transformation history found for the current user.',
  UNTITLED_RUN: 'Untitled run',
  NO_FILE_ID: 'No file id',
  UNKNOWN_STATUS: 'Unknown',
  UNABLE_TO_LOAD: 'Unable to load history',
} as const

// ─── Workspace steps ──────────────────────────────────────────────────────────
export const WORKSPACE_STEPS = [
  'Discovery',
  'Blueprint',
  'Build',
  'Validate',
  'Export',
] as const

// ─── Create Asset Modal ───────────────────────────────────────────────────────
export const CREATE_ASSET = {
  ASSET_NAME_LABEL: 'Asset Name',
  ASSET_NAME_PLACEHOLDER: 'e.g. Contract_Performance',
  SOURCE_TYPE_LABEL: 'Source Type',
  STEP1_NEXT: 'Next',
  STEP2_TITLE: 'Upload Files',
  UPLOAD_BTN: 'Upload',
  UPLOADING: 'Uploading...',
  BACK_BTN: 'Back',
  SUCCESS: 'Uploaded successfully!',
} as const

// ─── Edit Project Modal ───────────────────────────────────────────────────────
export const EDIT_PROJECT = {
  TITLE: 'Edit Project',
  DESC_LABEL: 'Description',
  DESC_PLACEHOLDER: 'Enter a brief description of the project',
  PRIORITY_LABEL: 'Priority',
  STATUS_LABEL: 'Status',
  COLLABORATORS_LABEL: 'Collaborators',
  USERS_PLACEHOLDER: 'Search users...',
  CANCEL_BTN: 'Cancel',
  SAVE_BTN: 'Save Changes',
  SAVING: 'Saving changes...',
} as const

// ─── Discovery Step ───────────────────────────────────────────────────────────
export const DISCOVERY = {
  TITLE: 'Discovery',
  NO_FILES: 'No files found',
  NO_FILES_SELECTED: 'No files selected',
  PROCEED_BTN: 'Proceed to Blueprint',
  LOADING_CONTENT: 'Loading content…',
} as const

// ─── Blueprint Step ───────────────────────────────────────────────────────────
export const BLUEPRINT = {
  COMPARE_BTN: 'Compare',
  LOADING_FILES: 'Loading files...',
  REFINE_WITH: 'Refine with Awgment',
} as const

// ─── Build Step ───────────────────────────────────────────────────────────────
export const BUILD = {
  COMPARE_BTN: 'Compare',
  NO_SOURCE: 'No source code available.',
  GENERATION_PENDING: 'Generation Pending',
  GENERATE_LABEL: 'Generate',
  NO_REFINED_CODE: 'No Refined Code Yet',
  REFINE_WITH: 'Refine with Awgment',
  CONFIG_TITLE: 'Build Configuration',
  TARGET_CODE_TYPE: 'Target Code Type',
  INSTRUCTIONS_LABEL: 'Add Instructions/Query',
  GENERATING: 'Generating...',
  SAVE_WHICH_VERSION: 'Save Which Version',
  LOADING_FILES: 'Loading files...',
} as const

// ─── Validation Step ──────────────────────────────────────────────────────────
export const VALIDATION = {
  COMPARE_BTN: 'Compare',
  DOWNLOAD_BTN: 'Download',
  NO_SOURCE: 'No source code available.',
  NO_TESTS: 'No test cases yet',
  GENERATE_TESTS_BTN: 'Generate Tests',
  SETTINGS_TITLE: 'Test Case Settings',
  INSTRUCTIONS_LABEL: 'Instructions (Optional)',
  LOADING_FILES: 'Loading files...',
} as const

// ─── Export Step ──────────────────────────────────────────────────────────────
export const EXPORT = {
  TITLE: 'Export',
  LOADING_FILES: 'Loading files...',
  LOADING_CONTENT: 'Loading…',
  GIT_DIALOG_TITLE: 'Push to Git',
  PREVIEW_TITLE: (name: string) => `Preview — ${name}`,
} as const

// ─── Migration pages ──────────────────────────────────────────────────────────
export const SOURCES_PAGE = {
  TITLE: 'Sources',
  LOADING: 'Loading sources...',
} as const

export const DESTINATIONS_PAGE = {
  TITLE: 'Destinations',
  LOADING: 'Loading destinations...',
} as const

export const CONNECTIONS_PAGE = {
  TITLE: 'Connections',
  LOADING: 'Loading connections...',
} as const

// ─── Migration page buttons ───────────────────────────────────────────────────
export const MIGRATION_BTNS = {
  ADD_SOURCE: '+ Add Source',
  ADD_DESTINATION: '+ Add Destination',
  NEW_CONNECTION: '+ New Connection',
} as const

// ─── Create Connection Modal ──────────────────────────────────────────────────
export const CREATE_CONNECTION = {
  SUGGESTED: 'Suggested',
  NOT_CONFIGURED: 'Not configured',
  ADD_LATER: 'Can be added later',
  CONFIGURED: 'Configured ✓',
  SKIP_NOTE: '— you can add it later.',
  SKIP_BTN: 'Skip for now',
  CONN_NAME_LABEL: 'Connection Name',
  CREATE_BTN: 'Create Connection',
  DESTINATION_OPTIONAL: 'Destination is',
  OPTIONAL_LABEL: 'optional',
} as const

// ─── AI Setup Assistant ───────────────────────────────────────────────────────
export const AI_SETUP = {
  NEXT_BTN: 'Next',
} as const

// ─── Settings target languages ────────────────────────────────────────────────
export const TARGET_LANGUAGES = [
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'java', label: 'Java' },
  { value: 'sql', label: 'SQL' },
] as const
